import React, { useState, useEffect, useRef, useContext } from "react";
import { LocalizationContext } from "../contexts/localization-context";
import { ArtsContext } from "../contexts/arts-context";
import ArtVoteButton from "./ArtVoteButon";
import Swiper from "swiper/dist/js/swiper.min";

const ArtOverlayDesktop = ({
    activeArt,
    setActiveArt,
    votedArtId,
    setVotedArtId,
    campaignName,
}) => {
    const [localizationState, setLocalizationState] = useContext(LocalizationContext);
    const [arts, dispatchToArts] = useContext(ArtsContext);

    const [isVisible, setIsVisible] = useState(false);
    const [scrollY, setScrollY] = useState(null);

    const swiperContainerRef = useRef(null);
    const swiperPrevRef = useRef(null);
    const swiperNextRef = useRef(null);
    const activeArtIndex =
        arts && activeArt ? arts.findIndex((art) => art.nodeId === activeArt.nodeId) : null;

    let swiper;

    const initSwiper = () => {
        swiper = new Swiper(swiperContainerRef.current, {
            initialSlide: activeArtIndex,
            speed: 800,
            slidesPerView: 1,
            spaceBetween: 40,
            navigation: {
                nextEl: swiperNextRef.current,
                prevEl: swiperPrevRef.current,
            },
            a11y: true,
            a11y: {
                prevSlideMessage: "Previous slide",
                nextSlideMessage: "Next slide",
                firstSlideMessage: "This is the first slide",
                lastSlideMessage: "This is the last slide",
                paginationBulletMessage: "Go to slide {{index}}",
            },
            on: {
                slideChange: function () {
                    setActiveArt(arts[swiper.activeIndex]);
                },
            },
        });
    };

    const lockScrollPosition = () => {
        let scrollPosition = window.scrollY || document.documentElement.scrollTop;
        setScrollY(scrollPosition);
        document.querySelector("body").classList.add("no-scroll-desktop");
        document.querySelector("body").style.top = `-${scrollPosition}px`;
    };

    const unlockScrollPosition = () => {
        document.querySelector("body").classList.remove("no-scroll-desktop");
        setTimeout(function () {
            document.querySelector("body").style.top = "auto";
            document.documentElement.scrollTop = scrollY;
        }, 0);
    };

    useEffect(() => {
        initSwiper();
        setIsVisible(true);
        lockScrollPosition();

        return () => {
            swiper.destroy();
        };
    }, []);

    const closeOverlay = () => {
        setIsVisible(false);
        unlockScrollPosition();
        setTimeout(() => {
            setActiveArt(null);
        }, 300);
    };

    return (
        <div
            className={`art-overlay z-100 bg-black-o-80 fixed top-0 left-0 h-full w-full flex opacity-0 fade${
                isVisible ? " fade-in" : ""
            }`}
        >
            <button className="fixed p-12 right-0 top-0 cursor-pointer z-40" onClick={closeOverlay}>
                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13">
                    <path
                        d="M11.843 10.556 7.601 6.314l4.242-4.243A1 1 0 1 0 10.43.657L6.186 4.899 1.944.657A1 1 0 1 0 .529 2.07l4.243 4.243L.53 10.556a1 1 0 1 0 1.415 1.415l4.242-4.243 4.243 4.243a1 1 0 1 0 1.414-1.415"
                        fill="#000"
                    />
                </svg>
            </button>
            <div className="w-4/5 lg:w-3/4 md:w-2/3 p-40 overflow-hidden">
                <div className="swiper-container" ref={swiperContainerRef}>
                    <div className="swiper-wrapper">
                        {arts.map((art) => (
                            <div key={art.nodeId} className="swiper-slide">
                                <img
                                    className="h-auto max-h-full centered relative"
                                    src={art.image}
                                    alt={art.authorName}
                                />
                            </div>
                        ))}
                    </div>
                    <div
                        className="absolute centered-y left-32 md:left-12 h-48 w-48 bg-black-o-80 z-10 rounded-full flex items-center justify-center border-2 border-white-o-50"
                        ref={swiperPrevRef}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="16"
                            viewBox="0 0 8 12"
                        >
                            <path d="m6 0 1.4 1.4L2.8 6l4.6 4.6L6 12 0 6z" fill="#fcfcfc" />
                        </svg>
                    </div>
                    <div
                        className="absolute centered-y right-32 md:right-12 h-48 w-48 bg-black-o-80 z-10 rounded-full flex items-center justify-center border-2 border-white-o-50"
                        ref={swiperNextRef}
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="10"
                            height="16"
                            viewBox="0 0 8 12"
                        >
                            <path
                                d="M1.833 0 .434 1.4l4.6 4.6-4.6 4.6 1.4 1.4 6-6z"
                                fill="#fcfcfc"
                            />
                        </svg>
                    </div>
                </div>
            </div>
            <div className="w-1/5 lg:w-1/4 md:w-1/3 relative bg-white p-32 md:px-16 flex flex-col justify-between items-center">
                <div className="flex flex-col justify-center items-center">
                    <span className="font-bold text-primary text-24">{activeArt.votes}</span>
                    <span className="mt-4 text-14">{localizationState.votes}</span>
                </div>
                <div className="flex flex-col justify-center items-center w-full">
                    <img
                        className="w-96 h-96 rounded-full box-content"
                        src={activeArt.authorImage}
                        alt={activeArt.authorName}
                    />
                    <span className="mt-8 text-14 text-gray-brown-dark">
                        {activeArt.authorName}
                    </span>
                    <p className="w-full mt-24 text-14 text-center">{activeArt.description}</p>
                    <ArtVoteButton
                        className="mt-24"
                        artId={activeArt.nodeId}
                        votedArtId={votedArtId}
                        setVotedArtId={setVotedArtId}
                        campaignName={campaignName}
                    />
                </div>
                <div>
                    <a
                        href={activeArt.authorSocialMediaUrl}
                        target="_blank"
                        className="link link--primary link--arrow-right outline-none focus:shadow-outline focus:rounded"
                    >
                        <span>{activeArt.authorSocialMediaText}</span>
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18.02 8.82">
                            <path
                                xmlns="http://www.w3.org/2000/svg"
                                d="M13.86.29a1,1,0,0,0-1.41,0,1,1,0,0,0,0,1.41l1.71,1.7H1a1,1,0,0,0,0,2H14.16l-1.71,1.7a1,1,0,0,0,0,1.41,1,1,0,0,0,.71.3,1,1,0,0,0,.7-.29L18,4.41Z"
                            ></path>
                        </svg>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default ArtOverlayDesktop;
